import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PageShell } from '../layout/page-shell.jsx';
import { useToast } from '../ui/toast.jsx';
import { ConfirmDialog } from '../ui/confirm-dialog.jsx';
import { EmptyState } from '../ui/empty-state.jsx';
import { PersonFormModal } from '../oss/person-form-modal.jsx';
import { MoveModal } from '../oss/move-modal.jsx';
import { EnrollTerminalModal } from '../users/enroll-terminal-modal.jsx';
import { HomeOnlyBadge } from '../channels/channel-badges.jsx';
import { api, ApiError } from '../../api/index.js';
import { formatUtcInTimezone } from '../../lib/time.js';

/**
 * Flat personnel list across the whole OSS tree. Useful for quick
 * lookups, filters by device-status, and bulk views. The OSS tree page
 * remains the primary management surface.
 */
export function PersonnelPage({ currentUser }) {
  const toast = useToast();
  const isAdmin = currentUser?.role === 'admin';

  const [persons, setPersons] = useState([]);
  const [unitsById, setUnitsById] = useState({});
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [filterDevice, setFilterDevice] = useState('all');     // all / yes / no
  const [filterUnitId, setFilterUnitId] = useState('');         // empty = any unit (includes subtree)
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput), 250);
    return () => clearTimeout(id);
  }, [searchInput]);

  const [editState, setEditState] = useState({ open: false, person: null, unit: null });
  const [moveState, setMoveState] = useState({ open: false, person: null });
  const [deleteState, setDeleteState] = useState({ open: false, person: null });
  // enrollState carries an extra `force` flag set to true when the operator
  // has already approved the revoke-and-rebind for a person whose previous
  // terminal is still active. EnrollTerminalModal threads that flag into
  // every mint() call so the backend skips the 409 duplicate-check.
  const [enrollState, setEnrollState] = useState({ open: false, person: null, force: false });
  // Separate state for the "this person already has a terminal — replace?"
  // dialog. Surfaced BEFORE EnrollTerminalModal so the operator decides
  // whether to revoke the previous binding before seeing the TTL form / QR.
  // Sourced from `person.terminal` enriched into the persons-list response
  // in §1.2, so detection is free (no extra round-trip on click).
  const [confirmReenrollState, setConfirmReenrollState] = useState({ open: false, person: null });
  // 2026-06-22 — Revoke action on the Actions column. Pops a
  // ConfirmDialog so the operator commits explicitly. After Round
  // 1.341 (mobile auto-wipe on close-code 4002) revoke is
  // destructive — the device wipes itself on next foreground and
  // cannot be undone from coord side.
  const [revokeTerminalState, setRevokeTerminalState] = useState({ open: false, person: null });
  // 2026-07-05 (Round 1.450) — Renew action on Actions column. Visible
  // when the terminal is 'expired' AND registry knows the pubkey (i.e.
  // was signed after Round 1.450 migration). Server re-signs the same
  // keypair with fresh notBefore/notAfter; UI shows a confirmation dialog
  // explaining the operation preserves identity (no re-enrollment needed).
  const [renewTerminalState, setRenewTerminalState] = useState({ open: false, person: null });
  // 2026-06-22 — physical-delete confirmation for an individual
  // revoked row inside the expanded Terminals history panel. Wired by
  // TerminalsHistoryDetails through a callback so the parent owns the
  // dialog state and post-action refresh.
  const [deleteTerminalState, setDeleteTerminalState] = useState({ open: false, person: null, terminal: null });
  // 2026-06-22 — per-row expand for the Terminals history details.
  // Click on the Device column toggles. Lazy-fetches the full registry
  // history (active + revoked) the first time a row is opened; cached
  // per-personId for the page lifetime so repeated open/close doesn't
  // re-hit the network. Refresh invalidates the cache.
  const [expandedPersonId, setExpandedPersonId] = useState(null);
  const [terminalsHistory, setTerminalsHistory] = useState({});
  // Mirror expandedPersonId in a ref so `refresh()` (which doesn't
  // depend on it via useCallback deps) can see the current value
  // without going stale and leaving the panel stuck on "Loading…".
  const expandedPersonIdRef = useRef(null);
  // { [personId]: { loading: bool, error: string|null, items: array } }

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [unitsRes, personsRes] = await Promise.all([
        api.units.tree(),
        api.persons.list({
          q: search,
          hasDevice: filterDevice === 'yes' ? 'true' : filterDevice === 'no' ? 'false' : '',
          unitIds: filterUnitId ? subtreeIds(unitsRes => unitsRes, filterUnitId) : '',
        }),
      ]);
      setTree(unitsRes.tree || []);
      const m = {};
      function walk(u) {
        m[u.id] = u;
        for (const c of u.children) walk(c);
      }
      for (const r of unitsRes.tree || []) walk(r);
      setUnitsById(m);
      // Apply unit-subtree filter on the client side too (the server already filtered).
      let list = personsRes.persons || [];
      if (filterUnitId) {
        const ids = collectSubtreeIds(unitsRes.tree || [], Number(filterUnitId));
        list = list.filter((p) => ids.has(p.unitId));
      }
      setPersons(list);
      // Invalidate the terminals-history cache so the next expand
      // re-fetches with the post-mutation state (revoke / return /
      // delete). If the panel is currently open for some person, we
      // also re-fetch synchronously here so the operator sees the
      // updated list immediately without an empty "Loading…" stuck
      // state — refresh() doesn't have expandedPersonId in its deps
      // (would re-fire on every expand), so we read via ref.
      const openId = expandedPersonIdRef.current;
      if (openId != null) {
        try {
          const data = await api.persons.listTerminals(openId);
          setTerminalsHistory({
            [openId]: { loading: false, error: null, items: data.terminals || [] },
          });
        } catch (err) {
          setTerminalsHistory({
            [openId]: {
              loading: false,
              error: err instanceof ApiError ? err.payload?.error || err.message : err?.message,
              items: [],
            },
          });
        }
      } else {
        setTerminalsHistory({});
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.payload?.error || err.message : err?.message);
    } finally {
      setLoading(false);
    }
  }, [search, filterDevice, filterUnitId]);

  // Toggle the expanded row + lazy-fetch its terminals history.
  const toggleExpand = useCallback(async (personId) => {
    const next = expandedPersonId === personId ? null : personId;
    setExpandedPersonId(next);
    expandedPersonIdRef.current = next;
    if (next == null) return;
    if (terminalsHistory[personId]) return; // cached
    setTerminalsHistory((prev) => ({
      ...prev,
      [personId]: { loading: true, error: null, items: [] },
    }));
    try {
      const data = await api.persons.listTerminals(personId);
      setTerminalsHistory((prev) => ({
        ...prev,
        [personId]: { loading: false, error: null, items: data.terminals || [] },
      }));
    } catch (err) {
      setTerminalsHistory((prev) => ({
        ...prev,
        [personId]: {
          loading: false,
          error: err instanceof ApiError ? err.payload?.error || err.message : err?.message,
          items: [],
        },
      }));
    }
  }, [expandedPersonId, terminalsHistory]);

  useEffect(() => { refresh(); }, [refresh]);

  const flatUnitsForSelect = useMemo(() => {
    const out = [];
    function walk(u, d) {
      out.push({ id: u.id, name: u.name, depth: d });
      for (const c of u.children) walk(c, d + 1);
    }
    for (const r of tree) walk(r, 0);
    return out;
  }, [tree]);

  const stats = useMemo(() => {
    const total = persons.length;
    const withDevice = persons.filter((p) => p.hasDevice).length;
    const callsigns = persons.filter((p) => p.callsign).length;
    return { total, withDevice, callsigns };
  }, [persons]);

  async function handleEditSave(payload) {
    await api.persons.update(editState.person.id, payload);
    toast.success('Person updated.');
    setEditState({ open: false, person: null, unit: null });
    await refresh();
  }

  async function handleMoveConfirm(targetUnitId) {
    if (!targetUnitId) return;
    await api.persons.move(moveState.person.id, targetUnitId);
    toast.success(`Moved "${moveState.person.displayName}".`);
    setMoveState({ open: false, person: null });
    await refresh();
  }

  async function handleDelete() {
    try {
      await api.persons.remove(deleteState.person.id);
      toast.success('Person deleted.');
      setDeleteState({ open: false, person: null });
      await refresh();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.payload?.error || err.message : 'Delete failed.');
    }
  }

  // 2026-06-22 — Revoke current active terminal binding for the person.
  // Backend (a) flips coord registry row to status='revoked' + bumps
  // policy version, (b) mirrors the revoke to the mesh microservice so
  // the per-node terminals row + live WS connection are killed too.
  async function handleRevokeTerminal() {
    const p = revokeTerminalState.person;
    const t = p?.terminal;
    if (!p || !t?.certSerial) {
      setRevokeTerminalState({ open: false, person: null });
      return;
    }
    try {
      await api.persons.revokeTerminal(p.id, t.certSerial);
      toast.success(`Terminal revoked for ${p.callsign || p.displayName || 'person'}.`);
      setRevokeTerminalState({ open: false, person: null });
      await refresh();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.payload?.error || err.message : 'Revoke failed.');
    }
  }

  // 2026-07-05 (Round 1.450) — Renew the terminal cert without
  // re-enrollment. Coord re-signs the SAME keypair with fresh dates,
  // registry swaps in the new PEM. The node picks up the new cert via
  // policy snapshot push and the mobile receives it via cert-pull
  // (Etape B). Terminal identity (uuid, pubkey, contacts,
  // cache) stays intact.
  async function handleRenewTerminal(validityDays) {
    const p = renewTerminalState.person;
    const t = p?.terminal;
    if (!p || !t?.certSerial) {
      setRenewTerminalState({ open: false, person: null });
      return;
    }
    try {
      const days = Math.max(1, Math.min(3650, Math.floor(Number(validityDays) || 365)));
      const out = await api.persons.renewTerminal(p.id, t.certSerial, { validityDays: days });
      toast.success(
        `Terminal renewed for ${p.callsign || p.displayName || 'person'} — new expiry ${out.notAfter?.slice(0, 10) || '?'}.`,
      );
      setRenewTerminalState({ open: false, person: null });
      await refresh();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.payload?.error || err.message : 'Renewal failed.');
    }
  }

  // 2026-06-22 — Physical delete of a revoked terminal record (Trash
  // icon inside the expanded Terminals history panel).
  async function handleDeleteTerminal() {
    const { person: p, terminal: t } = deleteTerminalState;
    if (!p || !t?.certSerial) {
      setDeleteTerminalState({ open: false, person: null, terminal: null });
      return;
    }
    try {
      await api.persons.deleteTerminal(p.id, t.certSerial);
      toast.success('Terminal record deleted.');
      setDeleteTerminalState({ open: false, person: null, terminal: null });
      await refresh();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.payload?.error || err.message : 'Delete failed.');
    }
  }

  return (
    <PageShell
      icon="fa-users"
      title="Personnel"
      subtitle="Flat view of all personnel across the OSS tree"
    >
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <StatCard icon="fa-users"          label="Total"        value={stats.total}      tone="accent" />
        <StatCard icon="fa-mobile-screen"  label="With device"  value={stats.withDevice} tone="success" />
        <StatCard icon="fa-tag"            label="With callsign" value={stats.callsigns} tone="mist" />
      </div>

      {/* Filters */}
      <div className="card mb-4 flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 rounded-2xl border border-line bg-white p-1">
            {['all', 'yes', 'no'].map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setFilterDevice(v)}
                className={
                  filterDevice === v
                    ? 'rounded-xl bg-accent-soft px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-accent'
                    : 'rounded-xl px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-mist hover:text-ink'
                }
              >
                {v === 'all' ? 'All' : v === 'yes' ? 'With device' : 'No device'}
              </button>
            ))}
          </div>
          <select
            className="field max-w-xs"
            value={filterUnitId}
            onChange={(e) => setFilterUnitId(e.target.value)}
          >
            <option value="">All units</option>
            {flatUnitsForSelect.map((u) => (
              <option key={u.id} value={u.id}>
                {'  '.repeat(u.depth)}
                {u.name}
              </option>
            ))}
          </select>
        </div>
        <div className="relative w-full sm:max-w-xs">
          <i className="fa-solid fa-magnifying-glass pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-mist" aria-hidden="true" />
          <input
            type="search"
            className="field pl-10"
            placeholder="Name, callsign, position, rank…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
      </div>

      {error && (
        <div role="alert" className="card mb-4 border-rose-200 bg-rose-soft px-5 py-4 text-sm text-rose-700">
          <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
          {error}
        </div>
      )}

      {loading && persons.length === 0 ? (
        <div className="card px-6 py-16 text-center text-mist">
          <span className="loader-spinner mx-auto mb-3 text-accent" aria-hidden="true" />
          <div className="text-sm">Loading personnel…</div>
        </div>
      ) : persons.length === 0 ? (
        <EmptyState
          icon="fa-users"
          title="No personnel match the filters"
          description={
            filterDevice !== 'all' || filterUnitId || search
              ? 'Try clearing filters or the search field.'
              : 'Add personnel from the OSS page.'
          }
        />
      ) : (
        // Round 1.345 (2026-06-23) — Personnel now groups persons by
        // unit, one card per unit. Operator request to make the
        // table easier to scan when there are several units. Each
        // unit-card carries its own (header + tbody) — the per-row
        // JSX below is unchanged, just wrapped in an outer
        // `Object.entries(personsByUnit).map(...)` so the same row
        // markup renders inside each card. Units sort by name
        // (case-insensitive); persons inside a unit keep the
        // server-side order (callsign / displayName).
        (() => {
          const personsByUnit = new Map();
          for (const p of persons) {
            if (!personsByUnit.has(p.unitId)) personsByUnit.set(p.unitId, []);
            personsByUnit.get(p.unitId).push(p);
          }
          const groups = Array.from(personsByUnit.entries())
              .map(([unitId, list]) => ({
                unitId,
                unit: unitsById[unitId],
                persons: list,
              }))
              .sort((a, b) => {
                const an = (a.unit?.name || '').toLowerCase();
                const bn = (b.unit?.name || '').toLowerCase();
                return an.localeCompare(bn);
              });
          return groups.map(({ unitId, unit, persons: unitPersons }) => (
            <div key={unitId} className="card overflow-hidden mb-4 last:mb-0">
              <div className="flex items-center justify-between border-b border-line bg-canvas/50 px-5 py-3">
                <div className="flex items-baseline gap-3">
                  <span className="text-sm font-semibold text-ink">
                    {unit?.name || `Unit #${unitId}`}
                  </span>
                  <span className="text-xs text-mist">
                    {unitPersons.length} {unitPersons.length === 1 ? 'person' : 'persons'}
                  </span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-line bg-canvas/60 text-left text-[11px] font-semibold uppercase tracking-wider text-mist">
                      <th className="px-5 py-3">Person</th>
                      <th className="px-5 py-3">Position / Callsign</th>
                      {/* Unit column dropped — every card is one unit, the
                         header above carries that info instead. */}
                      <th className="px-5 py-3">DMR ID</th>
                      <th className="px-5 py-3">Device</th>
                      <th className="px-5 py-3">Updated</th>
                      <th className="px-5 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unitPersons.map((p) => {
                  const unit = unitsById[p.unitId];
                  const isExpanded = expandedPersonId === p.id;
                  const histState = terminalsHistory[p.id];
                  return (
                    <Fragment key={p.id}>
                    <tr className={'border-b border-line last:border-b-0 hover:bg-canvas/40' + (isExpanded ? ' bg-canvas/30' : '')}>
                      <td className="px-5 py-3 align-middle">
                        <div className="flex items-center gap-3">
                          <div className={'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold ' + (p.hasDevice ? 'bg-accent-soft text-accent' : 'bg-canvas text-mist')}>
                            {(p.displayName || '?').charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              {p.rank && <span className="font-mono text-[10px] uppercase text-mist">{p.rank}</span>}
                              <span className="truncate font-semibold text-ink">{p.displayName}</span>
                            </div>
                            {p.homeNodeOnly && (
                              <div className="mt-0.5">
                                <HomeOnlyBadge active={p.homeNodeOnly} />
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3 align-middle">
                        <div className="text-ink">{p.position || '—'}</div>
                        {p.callsign && (
                          <div className="mt-0.5 inline-flex items-center gap-1 rounded border border-line bg-canvas px-1.5 py-0.5 font-mono text-[11px] text-mist">
                            <i className="fa-solid fa-tag text-[9px]" aria-hidden="true" />
                            {p.callsign}
                          </div>
                        )}
                      </td>
                      {/* Round 1.345 — Unit cell dropped; persons are
                          grouped by unit in a wrapper card. */}
                      {/* Round 65 — DMR ID column. Monospace, neutral
                          chip; "—" placeholder for legacy rows that
                          slip through without a DMR ID. */}
                      <td className="px-5 py-3 align-middle">
                        {p.dmrId != null ? (
                          <span className="inline-flex items-center gap-1 rounded border border-line bg-canvas px-1.5 py-0.5 font-mono text-[12px] text-ink">
                            <i className="fa-solid fa-walkie-talkie text-[10px] text-mist" aria-hidden="true" />
                            {p.dmrId}
                          </span>
                        ) : (
                          <span className="text-mist">—</span>
                        )}
                      </td>
                      <td
                        className="cursor-pointer px-5 py-3 align-middle"
                        onClick={() => toggleExpand(p.id)}
                        title="Click to view terminal history"
                      >
                        {(() => {
                          // Live terminal-binding state from the mesh
                          // microservice (joined in by GET /api/persons
                          // — see routes/persons.js). Lit badge = a
                          // terminal cert is currently active for this
                          // person; tooltip shows uuid + enrolled-at +
                          // last-seen for diagnosis. Falls back to the
                          // static `hasDevice` planning flag if the
                          // mesh is unreachable (terminal === null).
                          const t = p.terminal;
                          const tz = currentUser?.timezone || 'UTC';
                          let badge;
                          if (t && t.status === 'active') {
                            // 2026-07-05 (Round 1.450) — surface cert
                            // expiry on the Device tooltip so the operator
                            // can see how much time is left without
                            // opening the expand panel.
                            const expiresRel = (() => {
                              if (!t.certNotAfter) return null;
                              const days = Math.round(
                                (Date.parse(t.certNotAfter) - Date.now()) / 86_400_000,
                              );
                              if (!Number.isFinite(days)) return null;
                              if (days > 0) return `expires in ${days}d`;
                              if (days === 0) return 'expires today';
                              return `expired ${Math.abs(days)}d ago`;
                            })();
                            const tipParts = [
                              `Terminal ${(t.uuid || '').slice(0, 8)}`,
                              `enrolled ${formatUtcInTimezone(t.enrolledAt, tz)}`,
                              `last seen ${t.lastSeenAt ? formatUtcInTimezone(t.lastSeenAt, tz) : 'never'}`,
                            ];
                            if (expiresRel) tipParts.push(expiresRel);
                            badge = (
                              <span
                                title={tipParts.join(' · ')}
                                className="inline-flex items-center gap-1 rounded-full bg-accent-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-accent"
                              >
                                <i className="fa-solid fa-mobile-screen text-[9px]" aria-hidden="true" />
                                Device
                              </span>
                            );
                          } else if (t && t.status === 'expired') {
                            // 2026-07-05 (Round 1.450) — expired cert. Amber
                            // badge nudges the operator to renew before the
                            // device times out further. Renew action shows
                            // up in the Actions column when canRenew is true.
                            const tipParts = [
                              `Terminal ${(t.uuid || '').slice(0, 8)}`,
                              t.certNotAfter ? `expired ${formatUtcInTimezone(t.certNotAfter, tz)}` : 'expired',
                              t.canRenew ? 'ready to renew' : 'legacy cert — re-enroll required',
                            ];
                            badge = (
                              <span
                                title={tipParts.join(' · ')}
                                className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700 ring-1 ring-amber-200"
                              >
                                <i className="fa-solid fa-hourglass-end text-[9px]" aria-hidden="true" />
                                Expired
                              </span>
                            );
                          } else if (p.hasDevice) {
                            // Operator's planning flag says "should have
                            // a device" but no live binding yet — dim
                            // outline of the same badge to nudge the
                            // operator to follow up with the enrollment.
                            badge = (
                              <span
                                title="Planned device — no active terminal binding yet. Issue an enrollment ticket."
                                className="inline-flex items-center gap-1 rounded-full border border-line bg-canvas px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-mist"
                              >
                                <i className="fa-solid fa-mobile-screen text-[9px]" aria-hidden="true" />
                                Planned
                              </span>
                            );
                          } else {
                            badge = <span className="text-[11px] text-mist">—</span>;
                          }
                          return (
                            <span className="inline-flex items-center gap-2">
                              {badge}
                              <i
                                className={`fa-solid fa-chevron-${isExpanded ? 'up' : 'down'} text-[10px] text-mist`}
                                aria-hidden="true"
                              />
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-5 py-3 align-middle text-xs text-mist">
                        {formatUtcInTimezone(p.updatedAt, currentUser?.timezone || 'UTC')}
                      </td>
                      <td className="px-5 py-3 align-middle">
                        <div className="flex justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => setEditState({ open: true, person: p, unit })}
                            disabled={!isAdmin}
                            title="Edit"
                            className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-mist transition-colors hover:bg-canvas hover:text-ink disabled:opacity-30"
                          >
                            <i className="fa-solid fa-pen" aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setMoveState({ open: true, person: p })}
                            disabled={!isAdmin}
                            title="Move to another unit"
                            className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-mist transition-colors hover:bg-canvas hover:text-ink disabled:opacity-30"
                          >
                            <i className="fa-solid fa-arrows-up-down-left-right" aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              // Gate: if this person already has an active
                              // terminal (live state joined in by the
                              // GET /api/persons enrichment in §1.2), pop
                              // the replace-confirmation dialog instead of
                              // going straight to the TTL form. Operator
                              // must explicitly opt in to revoke-and-rebind
                              // before any QR is minted.
                              const hasActive = !!(p.terminal && p.terminal.status === 'active');
                              if (hasActive) {
                                setConfirmReenrollState({ open: true, person: p });
                              } else {
                                setEnrollState({ open: true, person: p, force: false });
                              }
                            }}
                            disabled={!isAdmin}
                            title="Issue terminal enrollment ticket"
                            className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-mist transition-colors hover:bg-accent-soft hover:text-accent disabled:opacity-30"
                          >
                            <i className="fa-solid fa-mobile-screen" aria-hidden="true" />
                          </button>
                          {/*
                            2026-06-22 — Revoke action. After Round 1.341
                            (mobile auto-wipe on close-code 4002) the
                            mobile device wipes itself to factory state
                            on next foreground. There is no Return /
                            un-revoke flow — once the device wipes, the
                            operator must mint a fresh enrollment ticket.
                          */}
                          {p.terminal && p.terminal.status === 'active' ? (
                            <button
                              type="button"
                              onClick={() => setRevokeTerminalState({ open: true, person: p })}
                              disabled={!isAdmin}
                              title="Revoke terminal — invalidates the cert, disconnects, and triggers device wipe"
                              className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-mist transition-colors hover:bg-rose-soft hover:text-rose-600 disabled:opacity-30"
                            >
                              <i className="fa-solid fa-ban" aria-hidden="true" />
                            </button>
                          ) : null}
                          {/*
                            2026-07-05 (Round 1.450) — Renew action. Visible
                            for BOTH active AND expired status when the
                            coord registry has the pubkey on file
                            (canRenew). Admin can preemptively renew a
                            healthy active cert (e.g. 3 weeks before its
                            notAfter) instead of waiting for expiry.
                            Re-signs the SAME keypair with fresh
                            notBefore/notAfter, preserving identity.
                            Not a re-enrollment.
                          */}
                          {p.terminal
                              && (p.terminal.status === 'active'
                                  || p.terminal.status === 'expired')
                              && p.terminal.canRenew ? (
                            <button
                              type="button"
                              onClick={() => setRenewTerminalState({ open: true, person: p })}
                              disabled={!isAdmin}
                              title={p.terminal.status === 'expired'
                                ? 'Renew cert — re-signs the same keypair. Terminal continues with existing identity + data.'
                                : 'Preemptive renewal — re-signs the same keypair with a fresh 365-day window. No re-enrollment.'}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-mist transition-colors hover:bg-emerald-50 hover:text-emerald-600 disabled:opacity-30"
                            >
                              <i className="fa-solid fa-arrows-rotate" aria-hidden="true" />
                            </button>
                          ) : null}
                          <button
                            type="button"
                            onClick={() => setDeleteState({ open: true, person: p })}
                            disabled={!isAdmin}
                            title="Delete"
                            className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-mist transition-colors hover:bg-rose-soft hover:text-rose-600 disabled:opacity-30"
                          >
                            <i className="fa-solid fa-trash" aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="border-b border-line bg-canvas/40 last:border-b-0">
                        <td colSpan={7} className="px-5 py-4">
                          <TerminalsHistoryDetails
                            personId={p.id}
                            state={histState}
                            viewerTimezone={currentUser?.timezone || 'UTC'}
                            isAdmin={isAdmin}
                            onDelete={(t) => setDeleteTerminalState({ open: true, person: p, terminal: t })}
                            onRenew={(t) => {
                              // 2026-07-05 (Round 1.450) — reuse the
                              // Renew ConfirmDialog. The dialog reads
                              // person.terminal (main-row scope), so
                              // synthesise it to point at the specific
                              // history row the operator clicked.
                              const synth = {
                                ...p,
                                terminal: {
                                  uuid: t.terminalUuid,
                                  status: t.status,
                                  certSerial: t.certSerial,
                                  certNotAfter: t.certNotAfter,
                                  canRenew: !!t.pubkeySpkiB64,
                                },
                              };
                              setRenewTerminalState({ open: true, person: synth });
                            }}
                          />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
                  </tbody>
                </table>
              </div>
            </div>
          ));
        })()
      )}

      <PersonFormModal
        open={editState.open}
        mode="edit"
        unitName={editState.unit?.name}
        initial={editState.person}
        onClose={() => setEditState({ open: false, person: null, unit: null })}
        onSave={handleEditSave}
      />

      <MoveModal
        open={moveState.open}
        mode="person"
        item={moveState.person}
        flatUnits={flatUnitsForSelect.map((u) => ({ ...u, disabled: false }))}
        onClose={() => setMoveState({ open: false, person: null })}
        onConfirm={handleMoveConfirm}
      />

      <ConfirmDialog
        open={deleteState.open}
        title={`Delete "${deleteState.person?.displayName || ''}"?`}
        message="This permanently removes the personnel record."
        confirmLabel="Delete"
        destructive
        onClose={() => setDeleteState({ open: false, person: null })}
        onConfirm={handleDelete}
      />

      <EnrollTerminalModal
        open={enrollState.open}
        person={enrollState.person}
        force={enrollState.force}
        onClose={() => {
          setEnrollState({ open: false, person: null, force: false });
          // Round 1.345 (2026-06-23) — refresh persons list after the
          // modal closes. The terminal-binding lifecycle (planned →
          // signed) happens inside the QR-scan flow but the list
          // status badge (Planned / signed_at / cert serial) only
          // re-renders on a fresh persons fetch. Without this the
          // operator had to manually F5 to see the just-enrolled
          // terminal show up as bound.
          refresh();
        }}
      />

      {/*
        Re-enrollment gate (§1.3). When the operator clicks the 📱 Issue-
        terminal button on a person who ALREADY has an active terminal
        bound, this dialog fires BEFORE the EnrollTerminalModal so the
        decision to revoke the old binding is the first thing they see —
        not a side-effect of clicking "Mint" inside the TTL form. On
        confirm, EnrollTerminalModal opens with force=true so every
        subsequent mint() inside it bypasses the 409 duplicate-check
        (backend revokes the old terminal atomically before issuing the
        new ticket). On "Keep old" nothing happens and no UI state
        changes — the operator returns to the Personnel list untouched.
      */}
      {(() => {
        const p = confirmReenrollState.person;
        const t = p?.terminal;
        const shortUuid = t?.uuid?.slice(0, 8) ?? '';
        const fmt = (iso) => {
          if (!iso) return 'never';
          try { return new Date(iso).toLocaleString(); } catch { return iso; }
        };
        const msg = p && t
          ? `${p.displayName || 'This person'} already has an active terminal `
            + `(${shortUuid}…), enrolled ${fmt(t.enrolledAt)}, `
            + `last seen ${fmt(t.lastSeenAt)}.\n\n`
            + `Revoking this terminal will immediately drop its connection — the old `
            + `device will see a "Terminal revoked, contact administrator" banner and `
            + `lose ability to send any data. Old chat history stays readable on the `
            + `device until the operator deletes the account.\n\n`
            + `Issue a new enrollment ticket?`
          : '';
        return (
          <ConfirmDialog
            open={confirmReenrollState.open}
            onClose={() => setConfirmReenrollState({ open: false, person: null })}
            onConfirm={() => {
              const next = confirmReenrollState.person;
              setConfirmReenrollState({ open: false, person: null });
              // Open the TTL/QR modal in force mode — the confirm we
              // just received is the operator's binding decision; the
              // TTL form just picks how long the new ticket is valid.
              setEnrollState({ open: true, person: next, force: true });
            }}
            title="Replace terminal binding?"
            message={msg}
            confirmLabel="Revoke old + issue new"
            cancelLabel="Keep old"
            destructive
          />
        );
      })()}

      {/*
        2026-06-22 — Revoke confirmation dialog. Standalone from the
        re-enrollment gate above: that revoke-and-mints, this is a pure
        status-flip on an existing row. After Round 1.341 the device
        auto-wipes on next foreground; the operator cannot undo this.
      */}
      {(() => {
        const p = revokeTerminalState.person;
        const t = p?.terminal;
        const fmt = (iso) => {
          if (!iso) return '—';
          try { return new Date(iso).toLocaleString(); } catch { return iso; }
        };
        const msg = p && t
          ? `Revoke ${p.callsign || p.displayName || 'person'}'s active terminal?\n\n`
            + `Terminal UUID: ${t.uuid?.slice(0, 8) || '?'}…\n`
            + `Enrolled: ${fmt(t.enrolledAt)}\n`
            + `Last seen: ${fmt(t.lastSeenAt)}\n\n`
            + `The cert will be marked revoked, the device disconnects immediately, `
            + `and the mobile app will wipe itself to factory state. This action `
            + `CANNOT be undone — the operator must mint a fresh enrollment ticket `
            + `to re-onboard the device.`
          : '';
        return (
          <ConfirmDialog
            open={revokeTerminalState.open}
            onClose={() => setRevokeTerminalState({ open: false, person: null })}
            onConfirm={handleRevokeTerminal}
            title={p ? `Revoke terminal for ${p.callsign || p.displayName || 'person'}?` : 'Revoke terminal?'}
            message={msg}
            confirmLabel="Revoke"
            cancelLabel="Cancel"
            destructive
          />
        );
      })()}

      {/*
        2026-07-05 (Round 1.450) — Renewal modal with days input. Distinct
        from re-enrollment: same keypair, same identity, same contacts +
        data. Only the X.509 wall-clock window advances. Non-destructive.
        Operator picks validity window; default 365.
      */}
      <RenewTerminalModal
        state={renewTerminalState}
        onClose={() => setRenewTerminalState({ open: false, person: null })}
        onConfirm={handleRenewTerminal}
      />

      {(() => {
        const p = deleteTerminalState.person;
        const t = deleteTerminalState.terminal;
        const fmt = (iso) => {
          if (!iso) return '—';
          try { return new Date(iso).toLocaleString(); } catch { return iso; }
        };
        const msg = p && t
          ? `Permanently delete this revoked terminal record?\n\n`
            + `Terminal UUID: ${(t.terminalUuid || '').slice(0, 8) || '?'}…\n`
            + `Cert serial:   ${(t.certSerial || '').slice(0, 12)}…\n`
            + `Signed:  ${fmt(t.signedAt)}\n`
            + `Revoked: ${fmt(t.revokedAt)}\n\n`
            + `Audit trail of who held this device is LOST. If the device is later `
            + `re-enrolled with the same UUID, it will get a NEW .onion that other `
            + `terminals don't know about. This action is irreversible.`
          : '';
        return (
          <ConfirmDialog
            open={deleteTerminalState.open}
            onClose={() => setDeleteTerminalState({ open: false, person: null, terminal: null })}
            onConfirm={handleDeleteTerminal}
            title="Delete terminal record?"
            message={msg}
            confirmLabel="Delete permanently"
            cancelLabel="Cancel"
            destructive
          />
        );
      })()}

    </PageShell>
  );
}

// ─── 2026-06-22 — Terminals history expand panel ───────────────────
//
// Rendered inside the colspan'd extra row when the operator clicks a
// person's Device column. Pulls the full registry history (active +
// revoked) from `/api/persons/:id/terminals` and shows it as a tight
// table — same UI rhythm as the audit-page expand panel: caller owns
// the state, this component just renders.
function TerminalsHistoryDetails({ personId, state, viewerTimezone, isAdmin, onDelete, onRenew }) {
  if (!state || state.loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-mist">
        <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
        Loading terminals…
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="flex items-center gap-2 text-xs text-rose-600">
        <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" />
        {state.error}
      </div>
    );
  }
  const items = state.items || [];
  if (items.length === 0) {
    return (
      <div className="text-xs text-mist">
        No terminal records — this person was never enrolled.
      </div>
    );
  }
  const fmt = (iso) => iso ? formatUtcInTimezone(iso, viewerTimezone) : '—';
  // 2026-07-05 (Round 1.450) — relative-days helper for the Expires
  // column. Positive → «in N days»; negative → «expired N days ago»;
  // 0 → «today». Returns null on invalid input so the caller shows «—».
  const relDays = (iso) => {
    if (!iso) return null;
    const ts = Date.parse(iso);
    if (!Number.isFinite(ts)) return null;
    const days = Math.round((ts - Date.now()) / 86_400_000);
    if (days === 0) return 'today';
    if (days > 0) return `in ${days} day${days === 1 ? '' : 's'}`;
    const past = Math.abs(days);
    return `expired ${past} day${past === 1 ? '' : 's'} ago`;
  };
  return (
    <div className="space-y-2">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-mist">
        Terminals history · {items.length}
      </div>
      <div className="overflow-x-auto rounded-xl border border-line bg-canvas/60">
        <table className="min-w-full text-[12px]">
          <thead>
            <tr className="border-b border-line text-left text-[10px] font-semibold uppercase tracking-wider text-mist">
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Terminal UUID</th>
              <th className="px-3 py-2">Cert serial</th>
              <th className="px-3 py-2">Signed</th>
              {/* 2026-07-05 (Round 1.450) — cert notAfter + relative days.
                  Amber-tinted when past-due to visually track expired-status. */}
              <th className="px-3 py-2">Expires</th>
              <th className="px-3 py-2">Revoked</th>
              <th className="px-3 py-2">Pubkey</th>
              <th className="px-3 py-2">Tor onion</th>
              <th className="px-3 py-2">Home node</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((t) => (
              <tr key={t.certSerial} className="border-b border-line last:border-b-0 hover:bg-canvas/40">
                <td className="px-3 py-2 align-top">
                  {t.status === 'active' ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-600">
                      <i className="fa-solid fa-circle-check text-[9px]" aria-hidden="true" />
                      Active
                    </span>
                  ) : t.status === 'expired' ? (
                    // 2026-07-05 (Round 1.450) — Amber expired badge.
                    // Signals the operator should renew via Personnel
                    // Actions column (fa-arrows-rotate).
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700 ring-1 ring-amber-200">
                      <i className="fa-solid fa-hourglass-end text-[9px]" aria-hidden="true" />
                      Expired
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-rose-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-rose-600">
                      <i className="fa-solid fa-ban text-[9px]" aria-hidden="true" />
                      Revoked
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 align-top font-mono text-[11px] text-ink">
                  {t.terminalUuid || '—'}
                </td>
                <td className="px-3 py-2 align-top font-mono text-[11px] text-mist" title={t.certSerial}>
                  {t.certSerial ? `${t.certSerial.slice(0, 12)}…` : '—'}
                </td>
                <td className="px-3 py-2 align-top text-xs text-mist">{fmt(t.signedAt)}</td>
                {/* 2026-07-05 (Round 1.450) — Expires cell: notAfter datetime
                    + relative days. Amber tint when past-due so an operator
                    can eyeball the whole history for imminent expirations. */}
                <td className="px-3 py-2 align-top text-xs">
                  {t.certNotAfter ? (
                    (() => {
                      const rel = relDays(t.certNotAfter);
                      const isPast = Date.parse(t.certNotAfter) < Date.now();
                      return (
                        <div className={isPast ? 'text-amber-700' : 'text-mist'}>
                          <div>{fmt(t.certNotAfter)}</div>
                          {rel ? (
                            <div className={`text-[10px] font-semibold uppercase tracking-wider ${isPast ? 'text-amber-700' : 'text-emerald-600'}`}>
                              {rel}
                            </div>
                          ) : null}
                        </div>
                      );
                    })()
                  ) : (
                    <span className="text-mist" title="Legacy row enrolled before Round 1.450 — no cert_not_after tracked">—</span>
                  )}
                </td>
                <td className="px-3 py-2 align-top text-xs text-mist">{fmt(t.revokedAt)}</td>
                <td className="px-3 py-2 align-top text-center">
                  {t.briarPubkeyB64 ? (
                    <i className="fa-solid fa-check text-[12px] text-emerald-600" title="Pubkey uploaded" aria-hidden="true" />
                  ) : (
                    <i className="fa-solid fa-minus text-[12px] text-mist" title="No pubkey" aria-hidden="true" />
                  )}
                </td>
                <td className="px-3 py-2 align-top font-mono text-[11px] text-mist" title={t.torOnion ? `${t.torOnion}.onion` : ''}>
                  {t.torOnion ? `${t.torOnion.slice(0, 12)}…` : '—'}
                </td>
                <td className="px-3 py-2 align-top text-xs text-mist">
                  #{t.homeNodeId ?? '—'}
                </td>
                <td className="px-3 py-2 align-top">
                  <div className="flex justify-end gap-1">
                    {/* 2026-07-05 (Round 1.450) — Renew action inside
                        the expand panel too, so the operator can
                        preemptively renew directly from the history
                        row (parity with the main-row Actions). Requires
                        pubkey on file (canRenew via pubkey_spki_b64). */}
                    {(t.status === 'active' || t.status === 'expired')
                        && t.pubkeySpkiB64 ? (
                      <button
                        type="button"
                        onClick={() => onRenew?.(t)}
                        disabled={!isAdmin}
                        title={t.status === 'expired'
                          ? 'Renew cert — re-signs same keypair with fresh window'
                          : 'Preemptive renew — re-signs same keypair with fresh 365-day window'}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-mist transition-colors hover:bg-emerald-50 hover:text-emerald-600 disabled:opacity-30"
                      >
                        <i className="fa-solid fa-arrows-rotate text-[11px]" aria-hidden="true" />
                      </button>
                    ) : null}
                    {t.status === 'revoked' ? (
                      <button
                        type="button"
                        onClick={() => onDelete?.(t)}
                        disabled={!isAdmin}
                        title="Delete this record permanently"
                        className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-mist transition-colors hover:bg-rose-soft hover:text-rose-600 disabled:opacity-30"
                      >
                        <i className="fa-solid fa-trash text-[11px]" aria-hidden="true" />
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-mist">
        Hint: only one row is allowed in <em>active</em> per person (1 person = 1 active terminal).
        Use the Revoke / Return buttons in the Actions column to flip an active row.
      </div>
    </div>
  );
}

// 2026-07-05 (Round 1.450) — Renew terminal cert modal.
//
// Distinct from ConfirmDialog because we need an operator-editable
// validity-days input. Same-keypair re-sign: identity of the terminal
// (uuid, pubkey, contacts, chat history, cache) stays intact; only the
// X.509 wall-clock window advances. Non-destructive.
function RenewTerminalModal({ state, onClose, onConfirm }) {
  const [days, setDays] = useState('365');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  // Reset input every time the modal opens (fresh 365 default).
  useEffect(() => {
    if (state.open) {
      setDays('365');
      setErr(null);
      setSaving(false);
    }
  }, [state.open]);

  useEffect(() => {
    if (!state.open) return;
    function onKey(e) { if (e.key === 'Escape' && !saving) onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state.open, onClose, saving]);

  if (!state.open) return null;

  const p = state.person;
  const t = p?.terminal;
  const label = p?.callsign || p?.displayName || 'person';
  const fmt = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  async function onSubmit(e) {
    e.preventDefault();
    setErr(null);
    const n = Math.floor(Number(days));
    if (!Number.isFinite(n) || n < 1) {
      setErr('Validity must be at least 1 day.');
      return;
    }
    if (n > 3650) {
      setErr('Validity too large (max 3650 days).');
      return;
    }
    setSaving(true);
    try {
      await onConfirm(n);
    } catch (e2) {
      setErr(e2?.message || 'Renewal failed.');
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}
    >
      <form
        onSubmit={onSubmit}
        className="w-full max-w-md rounded-2xl bg-surface p-6 shadow-xl ring-1 ring-line"
      >
        <header className="mb-5 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
            <i className="fa-solid fa-arrows-rotate" aria-hidden="true" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-ink">
              Renew cert for {label}
            </h2>
            <p className="text-[11px] text-mist">Non-destructive re-sign, same keypair</p>
          </div>
        </header>

        {t ? (
          <div className="mb-4 rounded-lg bg-canvas/60 px-3 py-2.5 text-[12px] text-mist ring-1 ring-line">
            <div><span className="text-ink">Terminal:</span> <span className="font-mono">{t.uuid?.slice(0, 12) || '?'}…</span></div>
            <div><span className="text-ink">Current status:</span> {t.status || '—'}</div>
            <div><span className="text-ink">Current expiry:</span> {fmt(t.certNotAfter)}</div>
          </div>
        ) : null}

        <div className="space-y-4">
          <div>
            <label htmlFor="renew_days" className="field-label">Validity (days)</label>
            <input
              id="renew_days"
              type="number"
              min="1"
              max="3650"
              step="1"
              className="field"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              disabled={saving}
              autoFocus
            />
            <p className="mt-1.5 flex items-center gap-1.5 text-[11px] text-mist">
              <i className="fa-regular fa-circle-info" aria-hidden="true" />
              Default 365. Server clamps to CA_TERMINAL_CERT_TTL_MAX_DAYS ({3650}).
            </p>
          </div>
          <div className="rounded-lg bg-emerald-50 p-3 text-[12px] text-emerald-800 ring-1 ring-emerald-200">
            Terminal identity (uuid, pubkey, contacts, chat history, cache) stays intact.
            No re-enrollment, no QR scan, no data loss. Once the new cert propagates
            to the home node, the device continues automatically.
          </div>
          {err ? (
            <div className="rounded-lg bg-rose-soft px-3 py-2 text-[12px] text-rose-700 ring-1 ring-rose-200">
              {err}
            </div>
          ) : null}
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg px-4 py-2 text-sm text-mist hover:bg-canvas hover:text-ink disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {saving ? (
              <><i className="fa-solid fa-spinner fa-spin" aria-hidden="true" /> Renewing…</>
            ) : (
              <><i className="fa-solid fa-arrows-rotate" aria-hidden="true" /> Renew for {Number(days) || '?'} days</>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

function collectSubtreeIds(tree, rootId) {
  const set = new Set();
  function walk(u, inside) {
    const here = inside || u.id === rootId;
    if (here) set.add(u.id);
    for (const c of u.children) walk(c, here);
  }
  for (const r of tree) walk(r, false);
  return set;
}

// Unused but kept for future server-side subtree filtering hooks.
// eslint-disable-next-line no-unused-vars
function subtreeIds() {
  return undefined;
}

function StatCard({ icon, label, value, tone = 'accent' }) {
  const tones = {
    accent:  'bg-accent-soft text-accent',
    success: 'bg-success-soft text-success',
    warning: 'bg-warning-soft text-warning',
    mist:    'bg-canvas text-mist',
  };
  return (
    <div className="card flex items-center justify-between px-5 py-4">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wider text-mist">{label}</div>
        <div className="mt-0.5 text-2xl font-semibold tracking-tight text-ink">{value}</div>
      </div>
      <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${tones[tone]}`}>
        <i className={`fa-solid ${icon} text-lg`} aria-hidden="true" />
      </div>
    </div>
  );
}
